<script lang="ts">
	import type { InventoryLocation, SearchField } from 'utility/search-fields'
	import type { SvelteSet } from 'svelte/reactivity'
	import type { i18n } from 'types/common'

	import { loadLocationsForStore } from 'utility/search-fields'
	import { klona } from 'klona'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Pin from 'components/search/Pin.svelte'
	import { untrack, type ComponentProps } from 'svelte'
	import { getContext } from 'svelte'

	interface Props extends Omit<
		ComponentProps<typeof Autocomplete<InventoryLocation | null>>,
		'value' | 'options' | 'getLabel' | 'placeholder'
	> {
		searchField: SearchField
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required
		selectedSearchFieldsKeys: SvelteSet<SearchField['key']> //Required
		locationList?: Promise<Array<InventoryLocation>> | Array<InventoryLocation>
		inventoryLocation?: InventoryLocation | null
		initialValue?: string
		stores: Array<number>
		disabled?: boolean
	}

	let {
		searchField,
		inEditMode,
		pinnedSearchFields = $bindable(),
		selectedSearchFieldsKeys = $bindable(),
		locationList = $bindable([]),
		inventoryLocation = $bindable(null),
		initialValue,
		stores,
		disabled = false,
		...rest
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	let previousStores: Array<number> | null = null //default to the same as the original store array

	async function loadLocations(storeIds: Array<number>) {
		//TODO: we can remove this if we fix whatever bug in the autocomplete component makes it be slow when the autocomplete has a `value` and the `options` change
		//I've put timers here, thinking that the amount of data we get from the API was causing the slowness, but it's not.
		//It's the autocomplete component itself and I couldn't figure out what was causing it.
		if (
			previousStores &&
			previousStores.length === stores.length &&
			previousStores.every(storeId => stores.includes(storeId))
		) {
			previousStores = klona(stores)
			return
		}

		previousStores = klona(stores)
		if (storeIds.length) {
			locationList = loadLocationsForStore(storeIds)
			locationList.then(locations => {
				locationList = locations

				if (initialValue) {
					inventoryLocation = locations.find(location => location.name === initialValue) ?? null
				} else if (
					inventoryLocation &&
					locationList &&
					!locationList.find(locationListItem => locationListItem.id === inventoryLocation?.id)
				) {
					inventoryLocation = null
				}
			})
		} else {
			locationList = []
			inventoryLocation = null
		}
	}

	$effect(() => {
		stores
		untrack(() => loadLocations(stores))
	})
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	<Autocomplete
		{...rest}
		label={translate('common:location', 'Location')}
		options={locationList}
		emptyValue={null}
		disabled={disabled || !stores.length || inEditMode}
		placeholder={stores.length
			? `-- ${translate('search.selectALocation', 'Select a Location')} --`
			: translate('search.chooseAtLeastOneStoreFirst', 'Choose at least one store first')}
		bind:value={inventoryLocation}
		getLabel={option => (option ? (option?.name ?? '') : '')}
	>
		{#snippet hint()}
			<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
		{/snippet}
	</Autocomplete>
</EditModeControls>
